import { useState, useEffect } from "react";

const DEFAULT_COMMUNITIES = [
  { id: "r1", platform: "Reddit", name: "r/Jacksonville", url: "https://reddit.com/r/Jacksonville", category: "Local", notes: "Main Jacksonville sub. Monitor for housing questions.", active: true },
  { id: "r2", platform: "Reddit", name: "r/jacksonville", url: "https://reddit.com/r/jacksonville", category: "Local", notes: "Alternate Jacksonville sub.", active: true },
  { id: "r3", platform: "Reddit", name: "r/renting", url: "https://reddit.com/r/renting", category: "Renting", notes: "National renting sub. Look for FL-specific threads.", active: true },
  { id: "r4", platform: "Reddit", name: "r/Landlord", url: "https://reddit.com/r/Landlord", category: "Renting", notes: "Landlord perspective. Engage carefully.", active: true },
  { id: "r5", platform: "Reddit", name: "r/TenantHelp", url: "https://reddit.com/r/TenantHelp", category: "Renting", notes: "Tenants seeking advice. High intent.", active: true },
  { id: "r6", platform: "Reddit", name: "r/FloridaMan", url: "https://reddit.com/r/FloridaMan", category: "Local", notes: "Occasional housing threads. Low priority.", active: false },
  { id: "r7", platform: "Reddit", name: "r/realestateinvesting", url: "https://reddit.com/r/realestateinvesting", category: "Real Estate", notes: "Investor perspective. SFR rental discussions.", active: false },
  { id: "r8", platform: "Reddit", name: "r/FirstTimeHomeBuyer", url: "https://reddit.com/r/FirstTimeHomeBuyer", category: "Real Estate", notes: "People leaving rentals. Share experiences.", active: true },
  { id: "f1", platform: "Facebook", name: "Jacksonville FL Rentals", url: "https://facebook.com/groups", category: "Local", notes: "Search Facebook for this group. High engagement.", active: true },
  { id: "f2", platform: "Facebook", name: "Jacksonville Florida Community", url: "https://facebook.com/groups", category: "Local", notes: "General local group with housing posts.", active: true },
  { id: "f3", platform: "Facebook", name: "Renters Rights Florida", url: "https://facebook.com/groups", category: "Renting", notes: "Advocacy-focused. Great for credibility.", active: true },
  { id: "n1", platform: "Nextdoor", name: "Your Neighborhood", url: "https://nextdoor.com", category: "Local", notes: "Hyper-local. Neighbors discussing property mgmt.", active: true },
  { id: "t1", platform: "TikTok", name: "Renter TikTok", url: "https://tiktok.com", category: "Content", notes: "Post short-form content. #rentertok #apartmentreview", active: true },
];

const DEFAULT_SEARCHES = [
  { id: "s1", platform: "Reddit", query: "property management Jacksonville", url: "https://www.reddit.com/search/?q=property+management+jacksonville&sort=new", frequency: "Daily" },
  { id: "s2", platform: "Reddit", query: "rental review house", url: "https://www.reddit.com/search/?q=rental+review+house&sort=new", frequency: "Daily" },
  { id: "s3", platform: "Reddit", query: "Invitation Homes review", url: "https://www.reddit.com/search/?q=invitation+homes+review&sort=new", frequency: "Weekly" },
  { id: "s4", platform: "Reddit", query: "Progress Residential complaints", url: "https://www.reddit.com/search/?q=progress+residential+complaints&sort=new", frequency: "Weekly" },
  { id: "s5", platform: "Reddit", query: "American Homes 4 Rent review", url: "https://www.reddit.com/search/?q=american+homes+4+rent+review&sort=new", frequency: "Weekly" },
  { id: "s6", platform: "Reddit", query: "worst property management company", url: "https://www.reddit.com/search/?q=worst+property+management+company&sort=new", frequency: "Daily" },
  { id: "s7", platform: "Reddit", query: "renting a house tips", url: "https://www.reddit.com/search/?q=renting+a+house+tips&sort=new", frequency: "Weekly" },
  { id: "s8", platform: "Google", query: "site:reddit.com Jacksonville rental problems", url: "https://www.google.com/search?q=site%3Areddit.com+Jacksonville+rental+problems&tbs=qdr:w", frequency: "Weekly" },
  { id: "s9", platform: "Google", query: "site:reddit.com property management company reviews", url: "https://www.google.com/search?q=site%3Areddit.com+property+management+company+reviews&tbs=qdr:w", frequency: "Weekly" },
  { id: "s10", platform: "Twitter/X", query: "property management terrible", url: "https://twitter.com/search?q=property+management+terrible&f=live", frequency: "Daily" },
];

const DEFAULT_TEMPLATES = [
  {
    id: "t1",
    name: "Someone asking for rental reviews",
    scenario: "User asks if anyone has experience with a specific rental or management company",
    response: `I actually built a site for exactly this — it's called DoorReport, where tenants can leave reviews on specific rental houses, not just the company overall. It's still early but we're growing the Jacksonville area right now. If you've rented from them, your review would help other people in the same spot. [link]`,
  },
  {
    id: "t2",
    name: "Someone complaining about property mgmt",
    scenario: "User venting about a bad property management experience",
    response: `That sounds really frustrating, sorry you're dealing with that. If you want to warn other renters, there's a site called DoorReport where you can leave a review tied to the actual address — so future tenants can see what they're getting into before they sign. Might save someone else from the same experience.`,
  },
  {
    id: "t3",
    name: "Someone moving to Jacksonville",
    scenario: "User asking about neighborhoods or rentals in Jacksonville",
    response: `Welcome! One tip — before you sign a lease on a house, try to look up reviews from previous tenants. I'd recommend checking DoorReport (doorreports.com) where people review specific rental addresses. The big property management companies here (Invitation Homes, Progress, etc.) can vary a lot house by house.`,
  },
  {
    id: "t4",
    name: "General renting advice thread",
    scenario: "User asking for tips on renting their first house",
    response: `One thing most people skip — look up reviews on the specific property AND the management company. Sites like DoorReport let tenants review individual houses, which is way more useful than a Google review on the company page. A company might be great in one neighborhood and terrible in another.`,
  },
  {
    id: "t5",
    name: "Thread about tenant rights / Fair Housing",
    scenario: "Discussion about tenant protections or rental scams",
    response: `Transparency is such a big part of this. One thing that helps is having a central place where tenants can share their actual experiences at specific addresses. I've been working on DoorReport for exactly this — tenant-first reviews on rental houses managed by the big property management companies. The more people share, the harder it is for bad actors to hide.`,
  },
];

const STORAGE_KEY = "doorreport-outreach";

function loadData() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveData(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    console.error("Storage save error:", e);
  }
}

function AddModal({ title, fields, onSubmit, onClose }) {
  const [form, setForm] = useState({});

  const handleSubmit = (e) => {
    e.preventDefault();
    const requiredFields = fields.filter((f) => f.key !== "url" && f.key !== "notes");
    const hasRequired = requiredFields.every((f) => form[f.key]?.trim());
    if (hasRequired) onSubmit(form);
  };

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  return (
    <div role="dialog" aria-modal="true" aria-label={title} style={styles.modalOverlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={styles.modal}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontFamily: "Georgia, serif", color: "#1a1a2e", fontSize: 18 }}>{title}</h3>
          <button onClick={onClose} aria-label="Close" style={{ ...styles.iconBtn, fontSize: 20 }}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          {fields.map((f) => (
            <div key={f.key} style={{ marginBottom: 14 }}>
              <label htmlFor={`modal-${f.key}`} style={styles.label}>{f.label}</label>
              {f.type === "select" ? (
                <select id={`modal-${f.key}`} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} style={styles.input}>
                  <option value="">Select...</option>
                  {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input id={`modal-${f.key}`} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} placeholder={f.placeholder} style={styles.input} />
              )}
            </div>
          ))}
          <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
            <button type="button" onClick={onClose} style={styles.cancelBtn}>Cancel</button>
            <button type="submit" style={styles.submitBtn}>Save</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function OutreachRadar() {
  const [tab, setTab] = useState("monitor");
  const [communities, setCommunities] = useState(DEFAULT_COMMUNITIES);
  const [searches, setSearches] = useState(DEFAULT_SEARCHES);
  const [templates] = useState(DEFAULT_TEMPLATES);
  const [engagements, setEngagements] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [showAddCommunity, setShowAddCommunity] = useState(false);
  const [showAddSearch, setShowAddSearch] = useState(false);
  const [showLogEntry, setShowLogEntry] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [filterPlatform, setFilterPlatform] = useState("All");

  useEffect(() => {
    const data = loadData();
    if (data) {
      if (data.communities) setCommunities(data.communities);
      if (data.searches) setSearches(data.searches);
      if (data.engagements) setEngagements(data.engagements);
    }
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (!loaded) return;
    saveData({ communities, searches, templates, engagements });
  }, [communities, searches, templates, engagements, loaded]);

  const todayStr = new Date().toISOString().split("T")[0];
  const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().split("T")[0];
  const todayEngagements = engagements.filter((e) => e.date === todayStr);
  const weekEngagements = engagements.filter((e) => e.date >= weekAgo);

  const copyTemplate = (id, text) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const addEngagement = (entry) => {
    setEngagements((prev) => [{ ...entry, id: "e" + Date.now(), date: todayStr }, ...prev]);
    setShowLogEntry(false);
  };

  return (
    <div style={styles.container}>
      <div style={styles.statsRow}>
        <div style={styles.statBox}><div style={styles.statNumber}>{todayEngagements.length}</div><div style={styles.statLabel}>Today</div></div>
        <div style={styles.statBox}><div style={styles.statNumber}>{weekEngagements.length}</div><div style={styles.statLabel}>This Week</div></div>
        <div style={styles.statBox}><div style={styles.statNumber}>{communities.filter((c) => c.active).length}</div><div style={styles.statLabel}>Active</div></div>
      </div>

      <nav style={styles.tabBar} role="tablist">
        {[
          { key: "monitor", label: "Communities" },
          { key: "search", label: "Search Links" },
          { key: "templates", label: "Templates" },
          { key: "log", label: "Engagement Log" },
        ].map((t) => (
          <button key={t.key} role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)} style={{ ...styles.tab, ...(tab === t.key ? styles.tabActive : {}) }}>
            {t.label}
          </button>
        ))}
      </nav>

      <div style={styles.content}>
        {tab === "monitor" && (
          <div>
            <div style={styles.sectionHeader}>
              <div>
                <h3 style={styles.sectionTitle}>Communities to Monitor</h3>
                <p style={styles.sectionDesc}>Check these daily for relevant conversations</p>
              </div>
              <button onClick={() => setShowAddCommunity(true)} style={styles.addBtn}>+ Add</button>
            </div>
            <div style={styles.filterRow}>
              {["All", "Reddit", "Facebook", "Nextdoor", "TikTok"].map((p) => (
                <button key={p} onClick={() => setFilterPlatform(p)} style={{ ...styles.filterChip, ...(filterPlatform === p ? styles.filterChipActive : {}) }}>{p}</button>
              ))}
            </div>
            {communities.filter((c) => filterPlatform === "All" || c.platform === filterPlatform).map((c) => (
              <div key={c.id} style={{ ...styles.card, opacity: c.active ? 1 : 0.5 }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
                      <span style={styles.platformBadge(c.platform)}>{c.platform}</span>
                      <span style={styles.categoryBadge}>{c.category}</span>
                    </div>
                    <a href={c.url} target="_blank" rel="noopener noreferrer" style={styles.communityName}>{c.name} →</a>
                    <p style={styles.communityNotes}>{c.notes}</p>
                  </div>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button onClick={() => setCommunities((prev) => prev.map((x) => x.id === c.id ? { ...x, active: !x.active } : x))} style={styles.iconBtn} title={c.active ? "Pause" : "Resume"}>{c.active ? "⏸" : "▶"}</button>
                    <button onClick={() => setCommunities((prev) => prev.filter((x) => x.id !== c.id))} style={{ ...styles.iconBtn, color: "#c44" }} title="Remove">×</button>
                  </div>
                </div>
              </div>
            ))}
            {showAddCommunity && (
              <AddModal title="Add Community"
                fields={[
                  { key: "platform", label: "Platform", type: "select", options: ["Reddit", "Facebook", "Nextdoor", "TikTok", "Twitter/X", "Other"] },
                  { key: "name", label: "Name", placeholder: "e.g., r/Tampa" },
                  { key: "url", label: "URL", placeholder: "https://..." },
                  { key: "category", label: "Category", type: "select", options: ["Local", "Renting", "Real Estate", "Content", "Other"] },
                  { key: "notes", label: "Notes", placeholder: "What to look for here" },
                ]}
                onSubmit={(c) => { setCommunities((prev) => [...prev, { ...c, id: "c" + Date.now(), active: true }]); setShowAddCommunity(false); }}
                onClose={() => setShowAddCommunity(false)}
              />
            )}
          </div>
        )}

        {tab === "search" && (
          <div>
            <div style={styles.sectionHeader}>
              <div>
                <h3 style={styles.sectionTitle}>Quick Search Links</h3>
                <p style={styles.sectionDesc}>Click to open pre-built searches. Look for threads to engage in.</p>
              </div>
              <button onClick={() => setShowAddSearch(true)} style={styles.addBtn}>+ Add</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12 }}>
              {searches.map((s) => (
                <div key={s.id} style={{ ...styles.card, display: "flex", flexDirection: "column", minHeight: 120 }}>
                  <div style={{ display: "flex", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
                    <span style={styles.platformBadge(s.platform)}>{s.platform}</span>
                    <span style={styles.frequencyBadge(s.frequency)}>{s.frequency}</span>
                  </div>
                  <p style={{ fontFamily: "Georgia, serif", fontSize: 14, fontWeight: 600, color: "#1a1a2e", margin: "0 0 12px", fontStyle: "italic", flex: 1 }}>"{s.query}"</p>
                  <div style={{ display: "flex", gap: 8 }}>
                    <a href={s.url} target="_blank" rel="noopener noreferrer" style={{ padding: "6px 14px", background: "#1a1a2e", color: "#fff", borderRadius: 6, fontSize: 12, fontWeight: 600, textDecoration: "none" }}>Open →</a>
                    <button onClick={() => setSearches((prev) => prev.filter((x) => x.id !== s.id))} style={{ ...styles.iconBtn, color: "#c44" }}>×</button>
                  </div>
                </div>
              ))}
            </div>
            {showAddSearch && (
              <AddModal title="Add Search"
                fields={[
                  { key: "platform", label: "Platform", type: "select", options: ["Reddit", "Google", "Twitter/X", "Facebook", "Other"] },
                  { key: "query", label: "Search Query", placeholder: "e.g., Progress Residential complaints" },
                  { key: "url", label: "Search URL", placeholder: "https://..." },
                  { key: "frequency", label: "Frequency", type: "select", options: ["Daily", "Weekly", "Monthly"] },
                ]}
                onSubmit={(s) => { setSearches((prev) => [...prev, { ...s, id: "s" + Date.now() }]); setShowAddSearch(false); }}
                onClose={() => setShowAddSearch(false)}
              />
            )}
          </div>
        )}

        {tab === "templates" && (
          <div>
            <div style={styles.sectionHeader}>
              <div>
                <h3 style={styles.sectionTitle}>Response Templates</h3>
                <p style={styles.sectionDesc}>Copy and customize for each conversation. Never paste verbatim — adapt to context.</p>
              </div>
            </div>
            {templates.map((t) => (
              <div key={t.id} style={{ ...styles.card, marginBottom: 14 }}>
                <h4 style={{ fontFamily: "Georgia, serif", fontSize: 16, fontWeight: 700, color: "#1a1a2e", margin: "0 0 4px" }}>{t.name}</h4>
                <p style={{ fontSize: 13, color: "#E8622A", fontWeight: 600, margin: "0 0 12px", fontStyle: "italic" }}>{t.scenario}</p>
                <div style={{ fontSize: 14, lineHeight: 1.7, color: "#444", background: "#faf9f5", border: "1px solid #eae9e2", borderRadius: 8, padding: "14px 16px", marginBottom: 12, whiteSpace: "pre-wrap" }}>{t.response}</div>
                <button onClick={() => copyTemplate(t.id, t.response)} style={{ padding: "8px 18px", background: "#1a1a2e", color: "#fff", border: "none", borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                  {copiedId === t.id ? "✓ Copied!" : "Copy to Clipboard"}
                </button>
              </div>
            ))}
            <div style={{ background: "#fef3e6", border: "1px solid #f0d4a8", borderRadius: 8, padding: "14px 18px", fontSize: 13, lineHeight: 1.6, color: "#6a4a1a", marginTop: 20 }}>
              <strong>Remember:</strong> Adapt every template to the specific conversation. Mention details from the thread. Never post the same response twice in the same community.
            </div>
          </div>
        )}

        {tab === "log" && (
          <div>
            <div style={styles.sectionHeader}>
              <div>
                <h3 style={styles.sectionTitle}>Engagement Log</h3>
                <p style={styles.sectionDesc}>Track where you've posted so you don't double-up</p>
              </div>
              <button onClick={() => setShowLogEntry(true)} style={styles.addBtn}>+ Log</button>
            </div>
            {engagements.length === 0 ? (
              <div style={{ textAlign: "center", padding: "60px 20px", color: "#aaa", fontSize: 15 }}>
                <p>No engagements logged yet.</p>
                <p style={{ fontSize: 13 }}>Click "+ Log" after you respond to a thread.</p>
              </div>
            ) : (
              engagements.map((e) => (
                <div key={e.id} style={styles.card}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", gap: 8, marginBottom: 6, alignItems: "center" }}>
                        <span style={styles.platformBadge(e.platform)}>{e.platform}</span>
                        <time style={{ fontSize: 12, color: "#8a8a7a", fontWeight: 600 }}>{e.date}</time>
                      </div>
                      <p style={{ fontFamily: "Georgia, serif", fontSize: 15, fontWeight: 700, color: "#1a1a2e", margin: "0 0 4px" }}>{e.community}</p>
                      <p style={{ fontSize: 13, color: "#555", margin: "0 0 6px" }}>{e.description}</p>
                      {e.url && <a href={e.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: "#E8622A", fontWeight: 600, textDecoration: "none" }}>View thread →</a>}
                    </div>
                    <button onClick={() => setEngagements((prev) => prev.filter((x) => x.id !== e.id))} style={{ ...styles.iconBtn, color: "#c44" }}>×</button>
                  </div>
                </div>
              ))
            )}
            {showLogEntry && (
              <AddModal title="Log Engagement"
                fields={[
                  { key: "platform", label: "Platform", type: "select", options: ["Reddit", "Facebook", "Nextdoor", "TikTok", "Twitter/X", "Other"] },
                  { key: "community", label: "Community / Group", placeholder: "e.g., r/Jacksonville" },
                  { key: "description", label: "What did you post?", placeholder: "Replied to thread about Progress Residential..." },
                  { key: "url", label: "Thread URL (optional)", placeholder: "https://..." },
                ]}
                onSubmit={addEngagement}
                onClose={() => setShowLogEntry(false)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: { fontFamily: "'Helvetica Neue', sans-serif", color: "#2a2a28" },
  statsRow: { display: "flex", gap: 20, marginBottom: 20 },
  statBox: { textAlign: "center", background: "#1a1a2e", padding: "12px 20px", borderRadius: 10 },
  statNumber: { fontSize: 22, fontWeight: 800, color: "#E8622A", fontFamily: "Georgia, serif" },
  statLabel: { fontSize: 10, color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: 1, marginTop: 2 },
  tabBar: { display: "flex", background: "#eae9e2", borderBottom: "2px solid #ddd9ce", overflowX: "auto", marginBottom: 24, borderRadius: "8px 8px 0 0" },
  tab: { padding: "10px 18px", border: "none", background: "transparent", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#8a8a7a", borderBottom: "2px solid transparent", marginBottom: -2, whiteSpace: "nowrap" },
  tabActive: { color: "#E8622A", borderBottomColor: "#E8622A" },
  content: {},
  sectionHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, flexWrap: "wrap", gap: 12 },
  sectionTitle: { fontFamily: "Georgia, serif", fontSize: 18, margin: 0, color: "#1a1a2e", fontWeight: 700 },
  sectionDesc: { fontSize: 13, color: "#8a8a7a", margin: "4px 0 0" },
  addBtn: { padding: "7px 16px", background: "#E8622A", color: "#fff", border: "none", borderRadius: 6, fontWeight: 700, fontSize: 13, cursor: "pointer" },
  filterRow: { display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" },
  filterChip: { padding: "5px 12px", borderRadius: 20, border: "1.5px solid #ccc", background: "transparent", fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#6a6a5a" },
  filterChipActive: { background: "#1a1a2e", color: "#fff", borderColor: "#1a1a2e" },
  card: { background: "#fff", border: "1px solid #e0ddd4", borderRadius: 10, padding: "14px 18px", marginBottom: 10 },
  platformBadge: (platform) => ({
    padding: "2px 10px", borderRadius: 4, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5,
    background: platform === "Reddit" ? "#FF4500" : platform === "Facebook" ? "#1877F2" : platform === "Nextdoor" ? "#00B246" : platform === "TikTok" ? "#010101" : platform === "Twitter/X" ? "#1DA1F2" : platform === "Google" ? "#4285F4" : "#888",
    color: "#fff",
  }),
  categoryBadge: { padding: "2px 10px", borderRadius: 4, fontSize: 11, fontWeight: 600, background: "#f0efe8", color: "#6a6a5a" },
  communityName: { fontFamily: "Georgia, serif", fontSize: 15, fontWeight: 700, color: "#1a1a2e", textDecoration: "none", display: "inline-block", marginBottom: 4 },
  communityNotes: { fontSize: 13, color: "#8a8a7a", margin: 0, lineHeight: 1.5 },
  iconBtn: { background: "none", border: "none", cursor: "pointer", fontSize: 18, padding: "2px 6px", borderRadius: 4, color: "#8a8a7a" },
  frequencyBadge: (freq) => ({ padding: "2px 8px", borderRadius: 4, fontSize: 10, fontWeight: 700, textTransform: "uppercase", background: freq === "Daily" ? "#E8622A" : "#eae9e2", color: freq === "Daily" ? "#fff" : "#6a6a5a" }),
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 300, padding: 20 },
  modal: { background: "#fff", borderRadius: 14, padding: "24px 28px", width: "100%", maxWidth: 440, boxShadow: "0 20px 60px rgba(0,0,0,0.25)" },
  label: { display: "block", fontSize: 12, fontWeight: 700, color: "#1a1a2e", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 },
  input: { width: "100%", padding: "9px 12px", border: "1.5px solid #ccc", borderRadius: 6, fontSize: 14, boxSizing: "border-box", outline: "none", background: "#faf9f5" },
  cancelBtn: { flex: 1, padding: "10px", background: "#f0efe8", color: "#6a6a5a", border: "none", borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: "pointer" },
  submitBtn: { flex: 1, padding: "10px", background: "#E8622A", color: "#fff", border: "none", borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: "pointer" },
};
